/* Words and small helpers for a Node.js app's parts (pages/sites/node/). */
import type { NodeProcessStatus, NodeSettings } from "../../../api/nodeApps";
import type { Tone } from "../../../ui/Badge";

export const APP_PARTS = ["processes", "variables", "routes", "jobs", "logs"] as const;
export type AppPartId = (typeof APP_PARTS)[number];
export const isAppPart = (p: string | undefined | null): p is AppPartId => (APP_PARTS as readonly string[]).includes(p ?? "");
export const APP_PART_LABEL: Record<AppPartId, string> = { processes: "Processes", variables: "Variables", routes: "Routes", jobs: "Scheduled jobs", logs: "Logs" };

/** A process's state as a pill: "Running", "Starting again", "Stopped". */
export function stateWords(p: Pick<NodeProcessStatus, "state" | "restarts">, deployed: boolean): { tone: Tone; text: string } {
  if (!deployed) return { tone: "neutral", text: "Not deployed yet" };
  switch (p.state) {
    case "active":
      return { tone: "good", text: "Running" };
    case "activating":
      return { tone: "warn", text: "Starting again" };
    case "deactivating":
      return { tone: "progress", text: "Stopping" };
    case "failed":
      return { tone: "bad", text: "Stopped after failing" };
    case "inactive":
      return { tone: "bad", text: "Stopped" };
  }
  return { tone: "neutral", text: p.state === "unknown" ? "Not known yet" : p.state };
}

const MB = 1024 * 1024;
/** "734 MB", "1.4 GB". */
export function bytes(n: number | null | undefined): string {
  if (n === null || n === undefined) return "–";
  if (n >= 1024 * MB) return `${(n / 1024 / MB).toFixed(n >= 10 * 1024 * MB ? 0 : 1)} GB`;
  if (n >= MB) return `${Math.round(n / MB)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

/** "512 MB", "2 GB" for a limit in MB. */
export const mbWords = (mb: number | null) => (mb === null ? "No limit" : mb >= 1024 && mb % 1024 === 0 ? `${mb / 1024} GB` : `${mb} MB`);

/** "1 hour", "7 days", "checked every time". */
export function maxAgeWords(sec: number): string {
  if (sec === 0) return "Checked every time";
  const unit = (n: number, w: string) => `${n} ${w}${n === 1 ? "" : "s"}`;
  if (sec % 86400 === 0) return unit(sec / 86400, "day");
  if (sec % 3600 === 0) return unit(sec / 3600, "hour");
  if (sec % 60 === 0) return unit(sec / 60, "minute");
  return unit(sec, "second");
}

export const MAX_AGES = [0, 300, 3600, 86400, 604800, 2592000, 31536000];

/** "every day at 10:00 UTC" for the common cron lines; the line itself otherwise. */
export function scheduleWords(schedule: string, tz: string): string {
  const s = schedule.trim();
  const macro: Record<string, string> = { "@hourly": "every hour", "@daily": "every day at 00:00", "@weekly": "every Sunday at 00:00", "@monthly": "on the 1st of every month at 00:00", "@yearly": "every January 1 at 00:00" };
  const zone = tz === "UTC" ? "UTC" : tz;
  if (macro[s]) return `${macro[s]}${s === "@hourly" ? "" : ` ${zone}`}`;
  const f = s.split(/\s+/);
  if (f.length !== 5) return s;
  const [min, hour, dom, mon, dow] = f;
  const num = (x: string | undefined) => (x !== undefined && /^\d+$/.test(x) ? Number(x) : null);
  const m = num(min);
  const h = num(hour);
  const at = m !== null && h !== null ? `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")} ${zone}` : null;
  if (at && dom === "*" && mon === "*" && dow === "*") return `every day at ${at}`;
  if (at && dom === "*" && mon === "*" && dow === "1-5") return `weekdays at ${at}`;
  if (m !== null && hour === "*" && dom === "*" && mon === "*" && dow === "*") return `every hour at :${String(m).padStart(2, "0")}`;
  const every = /^\*\/(\d+)$/.exec(min ?? "");
  if (every && hour === "*" && dom === "*" && mon === "*" && dow === "*") return `every ${every[1]} minutes`;
  return `${s} (${zone})`;
}

/** What changed between two settings, by part, for the save bar. */
export function changedParts(a: NodeSettings, b: NodeSettings): string[] {
  const same = (x: unknown, y: unknown) => JSON.stringify(x) === JSON.stringify(y);
  const out: string[] = [];
  if (!same(a.processes, b.processes) || a.web !== b.web) out.push("processes");
  if (!same(a.health, b.health) || a.test !== b.test) out.push("health and deploys");
  if (!same(a.persistent, b.persistent) || a.logsMaxMb !== b.logsMaxMb) out.push("folders and logs");
  if (!same(a.routes, b.routes) || !same(a.static, b.static) || !same(a.answers, b.answers) || !same(a.restarting, b.restarting) || !same(a.proxy, b.proxy)) out.push("routes");
  if (!same(a.jobs, b.jobs)) out.push("scheduled jobs");
  if (!same(a.packageSets ?? [], b.packageSets ?? [])) out.push("system packages");
  if (!same(a.snapshot ?? null, b.snapshot ?? null)) out.push("database snapshot");
  return out;
}

/**
 * The App's forms share one grid, so fields line up down a part and across parts: four even columns and a fixed one
 * for a row's Remove on a wide screen (kept when there's none, so every card's columns match), two columns on a
 * phone. A long field (a command) spans more.
 */
export const FORM_GRID = "grid grid-cols-2 gap-3 sm:grid-cols-[repeat(4,minmax(0,1fr))_5.75rem] sm:items-start";
/** A row's Remove button in FORM_GRID's last column, level with the fields' inputs (under their 20px labels). */
export const FORM_REMOVE = "col-span-2 flex sm:col-span-1 sm:col-start-5 sm:row-start-1 sm:pt-[29px]";

/** A positive whole number from a field, or null for an empty one. */
export const intOrNull = (v: string): number | null => (v.trim() === "" ? null : Number.isFinite(Number(v)) ? Math.round(Number(v)) : null);
